import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services';
import { useUIStore } from '../../../stores';

/**
 * Account update queries using React Query
 * 
 * Handles:
 *   - Full name updates
 *   - Country updates
 *   - Password changes
 *   - User data cache invalidation
 * 
 * Uses Laravel's built-in session handling
 * CSRF setup is handled automatically by apiService methods
 */
export const useUpdateFullName = () => {
  const { showSuccessToast, showErrorToast } = useUIStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (fullName) => {
      return await apiService.auth.updateFullName({ fullName });
    },
    onSuccess: (data) => {
      const { user } = data.data || {};
      
      // Update React Query cache with new user data
      queryClient.setQueryData(['auth', 'me'], user);

      showSuccessToast('Full Name Updated', 'Your full name has been updated successfully.');
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to update full name. Please try again.';
      showErrorToast('Update Failed', message);
    },
  });
};

export const useUpdateCountry = () => {
  const { showSuccessToast, showErrorToast } = useUIStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (country) => {
      return await apiService.auth.updateCountry({ country });
    },
    onSuccess: (data) => {
      const { user } = data.data || {};
      
      // Update React Query cache with new user data
      queryClient.setQueryData(['auth', 'me'], user);

      showSuccessToast('Country Updated', 'Your country has been updated successfully.');
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to update country. Please try again.';
      showErrorToast('Update Failed', message);
    },
  });
};

export const useChangePassword = (onFormReset) => {
  const { showSuccessToast, showErrorToast, closeAllModals } = useUIStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (passwordData) => {
      return await apiService.auth.updatePassword(passwordData);
    },
    onSuccess: (data) => {
      const { user } = data.data || {};
      
      // Update React Query cache with new user data
      queryClient.setQueryData(['auth', 'me'], user);

      // Invalidate the account status query to refresh UI
      queryClient.invalidateQueries({ queryKey: ['account', 'status'] });

      showSuccessToast('Password Changed', data.message || 'Your password has been changed successfully.');
      
      // Close all modals on successful password change
      closeAllModals();
    },
    onError: (error) => {
      // Only show toast for general errors (not field-level validation errors)
      if (!error.response?.data?.errors) {
        const message = error.response?.data?.message || 'Failed to change password. Please try again.';
        showErrorToast('Password Change Failed', message);
      }
      // Call the form reset callback when API error occurs
      if (onFormReset && typeof onFormReset === 'function') {
        onFormReset();
      }
    },
  });
};

export const useRequestEmailChange = (onFormReset, onSuccess) => {
  const { showSuccessToast, showErrorToast, closeAllModals } = useUIStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (emailData) => {
      return await apiService.auth.requestEmailChange(emailData);
    },
    onSuccess: (data) => {
      showSuccessToast('Email Change Requested', data.message || 'Email change verification sent to your new email address.');
      
      // Invalidate the account status query to refresh UI
      queryClient.invalidateQueries({ queryKey: ['account', 'status'] });

      // Call custom onSuccess callback if provided
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (!error.response?.data?.errors) {
        const message = error.response?.data?.message || 'Failed to request email change. Please try again.';
        showErrorToast('Email Change Failed', message);
      }
      if (onFormReset && typeof onFormReset === 'function') {
        onFormReset();
      }
    },
  });
};

export const useVerifyEmailChange = () => {
  const { showSuccessToast } = useUIStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data) => apiService.auth.verifyEmailChange(data),
    onSuccess: (data) => {
      showSuccessToast('Email Changed', 'Your email address has been successfully changed.');

      // Invalidate the account status query to refresh UI
      queryClient.invalidateQueries({ queryKey: ['account', 'status'] });
    },
  });
};

export const useAccountStatus = () => {
  return useQuery({
    queryKey: ['account', 'status'],
    queryFn: async () => {
      const response = await apiService.auth.getAccountStatus();
      
      return response.data;
    },
    enabled: true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
    retryDelay: 1000,
  });
};

export const useCancelEmailChange = () => {
  const { showSuccessToast, showErrorToast } = useUIStore();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      return await apiService.auth.cancelEmailChange();
    },
    onSuccess: (data) => {
      showSuccessToast('Request Cancelled', data.message || 'Email change request has been cancelled.');
      
      // Invalidate the account status to refresh the UI
      queryClient.invalidateQueries({ queryKey: ['account', 'status'] });
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to cancel email change request. Please try again.';
      showErrorToast('Cancellation Failed', message);
    },
  });
};